import API from "../api/api";
import { useNavigate } from "react-router-dom";

/**
 * Authentication Service
 * ----------------------
 * Helpers around the new distributor-user auth API:
 * - Login (no-scope token)
 * - Distributor list + scoped-token exchange
 * - Password flows: init, request reset, reset, change
 * - Claims introspection
 * - Token storage / decode
 */

export interface Login {
  email: string;
  password: string;
}

export interface DistributorAccess {
  distributorId: string;
  distributorName: string;
}

export class AuthenticationService {
  private static TOKEN_KEY = "auth-token";
  private static USER_EMAIL_KEY = "user-email";
  private static USER_ROLE_KEY = "user-role";
  private navigate = useNavigate();

  /*---------- LOGIN / LOGOUT ----------*/

  // Login with credentials. Returns { token } (no-scope).
  async userLogin(email: string, password: string): Promise<{ token: string }> {
    const response = await API.post(`/auth/distributor-user/login`, { email, password });
    API.defaults.headers.common["Authorization"] = `Bearer ${response.data.token}`;
    return response.data;
  }

  // List distributors available to the current (no-scope) user.
  async getDistributorsForUser(): Promise<DistributorAccess[]> {
    const response = await API.get(`/auth/distributor-user/distributors`);
    return response.data;
  }

  // Exchange the no-scope token for a distributor-scoped token.
  async getDistributorScopedToken(distributorId: string): Promise<{ token: string }> {
    const response = await API.get(
      `/auth/distributor-user/distributor-scoped-token?distributorId=${distributorId}`
    );
    API.defaults.headers.common["Authorization"] = `Bearer ${response.data.token}`;
    localStorage.setItem(AuthenticationService.TOKEN_KEY, response.data.token);
    return response.data;
  }

  // FE bootstrap / claim introspection (uses distributor-scoped token).
  async getClaims(): Promise<any> {
    const response = await API.get(`/auth/distributor-user/claims`);
    return response.data;
  }

  logout(): void {
    // Clear the in-memory Authorization header on the axios instance.
    // Without this, defaults still hold the previous user's bearer until a
    // full page reload — public requests (login, forgot-password, etc.) would
    // leak it to the BE.
    delete API.defaults.headers.common["Authorization"];
    localStorage.clear();
    this.navigate("/login");
  }

  /*---------- PASSWORD FLOWS ----------*/

  // Set password on first activation. The activation token comes from the URL
  // and is sent as a one-shot Bearer header — it is NOT persisted to localStorage,
  // so it can't clobber an existing logged-in session.
  async initPassword(password: string, activationToken: string): Promise<any> {
    const response = await API.post(
      `/auth/distributor-user/init-password`,
      { password },
      { headers: { Authorization: `Bearer ${activationToken}` } }
    );
    return response.data;
  }

  // Send a password-reset email (public).
  async requestPasswordReset(email: string): Promise<any> {
    const response = await API.post(`/auth/distributor-user/request-password-reset`, { email });
    return response.data;
  }

  // Complete password reset. The reset-purpose token comes from the URL and is
  // sent as a one-shot Bearer header (see initPassword note).
  async resetPassword(password: string, resetToken: string): Promise<any> {
    const response = await API.post(
      `/auth/distributor-user/reset-password`,
      { password },
      { headers: { Authorization: `Bearer ${resetToken}` } }
    );
    return response.data;
  }

  // Change password while logged in (distributor-scoped token).
  async changePassword(currentPassword: string, newPassword: string): Promise<any> {
    const response = await API.post(`/auth/distributor-user/change-password`, {
      currentPassword,
      newPassword,
    });
    return response.data;
  }

  /*---------- TOKEN HELPERS ----------*/

  saveToken(token: string): void {
    localStorage.removeItem(AuthenticationService.TOKEN_KEY);
    localStorage.setItem(AuthenticationService.TOKEN_KEY, token);
  }

  static getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  static getPropertyFromJtwToken(property: string) {
    const token = this.getToken();
    if (!token) return "";

    try {
      const decodedJWT = JSON.parse(atob(token.split(".")[1]));
      return decodedJWT[property] || "";
    } catch (error) {
      console.error("Error decoding JWT:", error);
      return "";
    }
  }

  /*---------- EMAIL HELPERS ----------*/

  saveEmail(email: string): void {
    localStorage.setItem(AuthenticationService.USER_EMAIL_KEY, email);
  }

  static getEmail(): string {
    const email = localStorage.getItem(this.USER_EMAIL_KEY);
    if (email) {
      return email;
    } else {
      return this.getPropertyFromJtwToken("email");
    }
  }

  /*---------- USER ROLE HELPERS ----------*/

  saveUserRole(role: string): void {
    localStorage.setItem(AuthenticationService.USER_ROLE_KEY, role);
  }

  static getUserRole(): string {
    const role = localStorage.getItem(this.USER_ROLE_KEY);
    if (role) return role;

    // New auth: role lives at the `role_name` claim (e.g. "Admin", "Tech").
    const roleName = this.getPropertyFromJtwToken("role_name");
    if (roleName) return roleName;

    // Legacy fallback for older tokens.
    const legacy = this.getPropertyFromJtwToken(
      "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
    );
    return Array.isArray(legacy) ? legacy[1] : legacy || "";
  }

  // Roles with admin-equivalent permissions in the dashboard UI.
  // HappyPay's admins have role "Admin"; Zapper's admins have role "Tech".
  static isAdminLikeRole(role: string = AuthenticationService.getUserRole()): boolean {
    return role === "Admin" || role === "Tech";
  }
}
